import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Funnel, FunnelChart
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, Activity, MousePointer, 
  Play, Link2, RefreshCw, Clock, Target, Zap, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import CornerstoneConversionCard from '../components/CornerstoneConversionCard';
import CornerstonePerformanceTrendCard from '../components/CornerstonePerformanceTrendCard';
import KPISparklineCard from '../components/KPISparklineCard';
import LeadMagnetConversionCard from '../components/LeadMagnetConversionCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#059669', '#0EA5E9', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4'];

const AnalyticsDashboard = () => {
  const { shouldShowData } = useDemoData();

  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [acquisitionFunnel, setAcquisitionFunnel] = useState(null);
  const [demoVsReal, setDemoVsReal] = useState(null);
  const [featureUsage, setFeatureUsage] = useState(null);
  const [retention, setRetention] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [period, setPeriod] = useState(30);

  const fetchAnalyticsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [summaryRes, funnelRes, demoRealRes, featuresRes, retentionRes, realtimeRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/summary?days=${period}`, { headers }),
        axios.get(`${API_URL}/api/analytics/funnel/acquisition?days=${period}`, { headers }),
        axios.get(`${API_URL}/api/analytics/funnel/demo-vs-real?days=${period}`, { headers }),
        axios.get(`${API_URL}/api/analytics/features/usage?days=${period}`, { headers }),
        axios.get(`${API_URL}/api/analytics/retention?days=${period}`, { headers }),
        axios.get(`${API_URL}/api/analytics/realtime`, { headers })
      ]);

      setSummary(summaryRes.data);
      setAcquisitionFunnel(funnelRes.data);
      setDemoVsReal(demoRealRes.data);
      setFeatureUsage(featuresRes.data);
      setRetention(retentionRes.data);
      setRealtime(realtimeRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Admin access required for analytics');
      } else {
        toast.error('Failed to load analytics data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
    
    // Refresh realtime data every 30 seconds
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      axios.get(`${API_URL}/api/analytics/realtime`, { 
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setRealtime(res.data))
        .catch(console.error);
    }, 30000);

  if (!shouldShowData) {
    return <EmptyDataState title="Analytics Dashboard" description="Connect your cloud account to see real data here." />;
  }


    return () => clearInterval(interval);
  }, [period]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalyticsData();
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-8 text-center">
            <h2 className="text-xl font-bold text-red-700">Access Denied</h2>
            <p className="text-red-600 mt-2">Analytics dashboard is only available to administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Activity className="w-7 h-7 text-emerald-500" />
            Analytics Dashboard
          </h1>
          <p className="text-slate-400 dark:text-slate-400">
            Track user behavior, conversions, and engagement
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Real-time Stats Banner */}
      {realtime && (
        <Card className="bg-emerald-500 bg-gradient-to-r from-emerald-500 to-teal-500">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-4 text-white">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                <span className="font-semibold">Live</span>
              </div>
              <div className="flex flex-wrap gap-8">
                <div>
                  <p className="text-emerald-100 text-xs">Active Now</p>
                  <p className="text-2xl font-bold">{realtime.active_now?.sessions || 0}</p>
                </div>
                <div>
                  <p className="text-emerald-100 text-xs">Last Hour</p>
                  <p className="text-2xl font-bold">{realtime.last_1h?.total_events || 0}</p>
                </div>
                <div>
                  <p className="text-emerald-100 text-xs">Last 24h Sessions</p>
                  <p className="text-2xl font-bold">{realtime.last_24h?.unique_sessions || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics — vibrant sparkline cards (Iter84ak polish pass) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpi-row">
        <KPISparklineCard
          testId="kpi-total-events"
          label="Total Events"
          value={summary?.total_events || 0}
          icon={Zap}
          tone="emerald"
          series={(summary?.daily_trend || []).map(d => d.events)}
        />
        <KPISparklineCard
          testId="kpi-unique-users"
          label="Unique Users"
          value={summary?.unique_users || 0}
          icon={Users}
          tone="blue"
          series={(summary?.daily_trend || []).map(d => d.users || 0)}
        />
        <KPISparklineCard
          testId="kpi-sessions"
          label="Sessions"
          value={summary?.unique_sessions || 0}
          icon={MousePointer}
          tone="violet"
          series={(summary?.daily_trend || []).map(d => d.sessions)}
        />
        <KPISparklineCard
          testId="kpi-return-rate"
          label="Return Rate"
          value={retention?.return_rate || 0}
          formatValue={(v) => `${v}%`}
          icon={TrendingUp}
          tone="amber"
        />
      </div>

      {/* Iter84t — Cornerstone Conversion (admin-only) */}
      <CornerstoneConversionCard days={period} />

      {/* Iter84z — Cornerstone Performance Trend chart */}
      <CornerstonePerformanceTrendCard days={period} />

      {/* Iter84an+ — Lead-magnet conversion roll-up (admin-only) */}
      <LeadMagnetConversionCard days={period} />

      <Tabs defaultValue="funnel" className="space-y-6">
        <TabsList>
          <TabsTrigger value="funnel">Conversion Funnel</TabsTrigger>
          <TabsTrigger value="demo-vs-real">Demo vs Real</TabsTrigger>
          <TabsTrigger value="features">Feature Usage</TabsTrigger>
          <TabsTrigger value="trends">Daily Trends</TabsTrigger>
        </TabsList>

        {/* Conversion Funnel Tab */}
        <TabsContent value="funnel" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Funnel Visualization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-500" />
                  Acquisition Funnel
                </CardTitle>
                <CardDescription>
                  User journey from landing page to account connection
                </CardDescription>
              </CardHeader>
              <CardContent>
                {acquisitionFunnel?.funnel && (
                  <div className="space-y-3">
                    {acquisitionFunnel.funnel.map((stage, idx) => (
                      <div key={stage.stage} className="relative">
                        <div 
                          className="h-12 rounded-lg flex items-center justify-between px-4 text-white font-medium"
                          style={{ 
                            backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
                            width: `${Math.max(20, (stage.count / (acquisitionFunnel.funnel[0]?.count || 1)) * 100)}%`,
                            minWidth: '200px'
                          }}
                        >
                          <span>{stage.stage}</span>
                          <span className="font-bold">{stage.count}</span>
                        </div>
                        {stage.conversion_rate !== null && (
                          <div className="absolute right-0 top-0 -mr-16 h-12 flex items-center">
                            <Badge variant="secondary" className="text-xs">
                              {stage.conversion_rate}%
                            </Badge>
                          </div>
                        )}
                      </div>
                    ))}
                    {acquisitionFunnel.overall_conversion !== null && (
                      <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                        <p className="text-sm text-emerald-700 dark:text-emerald-300">
                          Overall conversion: <strong>{acquisitionFunnel.overall_conversion}%</strong>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Funnel Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>Funnel Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <BarChart data={acquisitionFunnel?.funnel || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="stage" type="category" width={120} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#059669" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Demo vs Real Tab */}
        <TabsContent value="demo-vs-real" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Demo Mode Card */}
            <Card className="border-emerald-200 dark:border-cyan-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-cyan-300">
                  <Play className="w-5 h-5" />
                  Demo Mode
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <p className="text-4xl font-bold text-emerald-600">
                    {demoVsReal?.demo_mode?.starts || 0}
                  </p>
                  <p className="text-sm text-slate-400">Demo Starts</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-xl font-semibold text-slate-700 dark:text-slate-300">
                      {demoVsReal?.demo_mode?.feature_explorations || 0}
                    </p>
                    <p className="text-xs text-slate-400">Features Explored</p>
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-slate-700 dark:text-slate-300">
                      {demoVsReal?.demo_mode?.to_signup_conversions || 0}
                    </p>
                    <p className="text-xs text-slate-400">To Signup</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Real Accounts Card */}
            <Card className="border-emerald-200 dark:border-emerald-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                  <Link2 className="w-5 h-5" />
                  Real Accounts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <p className="text-4xl font-bold text-emerald-600">
                    {demoVsReal?.real_accounts?.successful_connections || 0}
                  </p>
                  <p className="text-sm text-slate-400">Connected</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-xl font-semibold text-slate-700 dark:text-slate-300">
                      {demoVsReal?.real_accounts?.connection_attempts || 0}
                    </p>
                    <p className="text-xs text-slate-400">Attempts</p>
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-slate-700 dark:text-slate-300">
                      {demoVsReal?.real_accounts?.success_rate || 0}%
                    </p>
                    <p className="text-xs text-slate-400">Success Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Comparison Card */}
            <Card>
              <CardHeader>
                <CardTitle>Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Demo Mode', value: demoVsReal?.comparison?.demo_starts || 0 },
                          { name: 'Real Accounts', value: demoVsReal?.comparison?.real_connections || 0 }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        <Cell fill="#8B5CF6" />
                        <Cell fill="#059669" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-center text-sm text-slate-400 mt-2">
                  Demo preference ratio: <strong>{demoVsReal?.comparison?.demo_preference_ratio || 0}x</strong>
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Signup Source */}
          <Card>
            <CardHeader>
              <CardTitle>Signup Sources</CardTitle>
              <CardDescription>Where are signups coming from?</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center gap-8">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center mb-2">
                    <p className="text-2xl font-bold text-emerald-600">
                      {demoVsReal?.signups_by_source?.from_demo || 0}
                    </p>
                  </div>
                  <p className="text-sm text-slate-400 dark:text-slate-400">From Demo</p>
                </div>
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-2">
                    <p className="text-2xl font-bold text-emerald-600">
                      {demoVsReal?.signups_by_source?.direct || 0}
                    </p>
                  </div>
                  <p className="text-sm text-slate-400 dark:text-slate-400">Direct</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feature Usage Tab */}
        <TabsContent value="features" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-emerald-500" />
                  Feature Clicks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <BarChart data={featureUsage?.feature_clicks?.slice(0, 10) || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="feature" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="clicks" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Engagement Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-emerald-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-600 dark:text-blue-400">AI Advisor Queries</p>
                      <p className="text-3xl font-bold text-emerald-700 dark:text-blue-300">
                        {featureUsage?.ai_advisor?.total_queries || 0}
                      </p>
                    </div>
                    <Zap className="w-10 h-10 text-emerald-500" />
                  </div>
                </div>

                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-amber-600 dark:text-amber-400">Recommendations</p>
                    <Badge variant="secondary">
                      {featureUsage?.recommendations?.application_rate || 0}% applied
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                        {featureUsage?.recommendations?.viewed || 0}
                      </p>
                      <p className="text-xs text-amber-600">Viewed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                        {featureUsage?.recommendations?.applied || 0}
                      </p>
                      <p className="text-xs text-emerald-600">Applied</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Daily Trends Tab */}
        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle>Daily Activity Trend</CardTitle>
              <CardDescription>Events and sessions over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                  <AreaChart data={summary?.daily_trend || []}>
                    <defs>
                      <linearGradient id="eventsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="sessionsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="events" 
                      name="Events"
                      stroke="#059669" 
                      fill="url(#eventsGradient)" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="sessions" 
                      name="Sessions"
                      stroke="#0EA5E9" 
                      fill="url(#sessionsGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Event Types Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Event Types Distribution</CardTitle>
          <CardDescription>Breakdown of all tracked events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(summary?.events_by_type || {}).slice(0, 12).map(([type, count], idx) => (
              <div 
                key={type}
                className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-800"
              >
                <p className="text-xs text-slate-400 truncate" title={type}>
                  {type.replace(/_/g, ' ')}
                </p>
                <p className="text-xl font-bold text-slate-700 dark:text-slate-300">
                  {count}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
